import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { MapPin, Mail, Phone, Building2 } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    productRequired: '',
    monthlyRequirement: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // API call will be implemented in backend phase
      toast.success("Inquiry Submitted", {
        description: "We'll get back to you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        name: '',
        company: '',
        productRequired: '',
        monthlyRequirement: '',
        phone: '',
        email: '',
        message: ''
      });
    } catch (error) {
      toast.error("Error", {
        description: "Failed to submit inquiry. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Get in touch for bulk inquiries and technical support
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Our Offices</h2>
              
              {/* Registered Office */}
              <Card className="mb-6 border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">REGISTERED OFFICE</h3>
                      <div className="flex items-start gap-2 mb-2">
                        <MapPin className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                        <p className="text-gray-700 leading-relaxed">
                          402/C2, Third Floor<br />
                          Omega Heights<br />
                          Dehradun, Uttarakhand – 248001<br />
                          India
                        </p>
                      </div>
                      <div className="mt-4 p-3 bg-blue-50 rounded">
                        <p className="text-sm font-semibold text-slate-900">
                          <strong>GSTIN:</strong> 05AAHCV3224B2ZT
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Corporate Office */}
              <Card className="mb-6 border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">CORPORATE OFFICE</h3>
                      <div className="flex items-start gap-2 mb-3">
                        <MapPin className="w-5 h-5 text-gray-600 flex-shrink-0 mt-1" />
                        <p className="text-gray-700 leading-relaxed">
                          F-14, Industrial Estate<br />
                          Delhi Road<br />
                          Saharanpur, Uttar Pradesh – 247001<br />
                          India
                        </p>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <Mail className="w-5 h-5 text-gray-600" />
                        <a 
                          href="mailto:sales@voltroncalcium.com" 
                          className="text-blue-600 hover:underline font-semibold"
                        >
                          sales@voltroncalcium.com
                        </a>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Phone className="w-5 h-5 text-gray-600" />
                        <a 
                          href="tel:+918679475554" 
                          className="text-blue-600 hover:underline font-semibold"
                        >
                          +91-86794 75554
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <div className="bg-white p-6 rounded-lg border-2">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Contact Information</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Phone:</strong> <a href="tel:+918679475554" className="text-blue-600 hover:underline">+91-86794 75554</a>
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Email:</strong> <a href="mailto:sales@voltroncalcium.com" className="text-blue-600 hover:underline">sales@voltroncalcium.com</a>
                </p>
                <h3 className="text-xl font-bold text-slate-900 mb-4 mt-6">Business Hours</h3>
                <p className="text-gray-700 mb-2">
                  <strong>Monday - Saturday:</strong> 9:00 AM - 6:00 PM
                </p>
                <p className="text-gray-700">
                  <strong>Sunday:</strong> Closed
                </p>
              </div>
            </div>

            {/* Inquiry Form */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Send an Inquiry</h2>
              
              <Card className="border-2">
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Your full name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company">Company *</Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="Company name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="productRequired">Product Required *</Label>
                      <Input
                        id="productRequired"
                        name="productRequired"
                        type="text"
                        value={formData.productRequired}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="e.g., Calcite Powder, Bentonite"
                      />
                    </div>

                    <div>
                      <Label htmlFor="monthlyRequirement">Monthly Requirement *</Label>
                      <Input
                        id="monthlyRequirement"
                        name="monthlyRequirement"
                        type="text"
                        value={formData.monthlyRequirement}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="e.g., 10 MT, 50 MT"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="+91 XXXXXXXXXX"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        rows={5}
                        placeholder="Tell us about your requirements, specifications, or any questions..."
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
